"use client";

import * as React from "react";
import {
    Select,
    SelectTrigger,
    SelectContent,
    SelectItem,
    SelectValue,
} from "components/ui/select";
import { cn } from "lib/utils";

/* ══════════════════════════════════════════════════════════════════════════
   SelectField — a drop-in for `<select>`.

   173 native selects remained across the app. A className styles the closed
   field only: the moment one opens, the option list is drawn by the operating
   system, so it ignores the theme entirely — grey-on-dark inside a light panel
   on a machine set to dark mode, and a different shape on every platform.

   Migrating them one at a time against the raw Radix parts means rewriting
   every handler (`e.target.value` → a bare value) and re-authoring every
   option. That is 173 chances to change behaviour by accident. So this takes
   the same children and the same onChange signature as the element it
   replaces, and a migration becomes a tag rename:

       <select value={x} onChange={e => setX(e.target.value)}>
         <option value="">All</option>
         <option value="a">A</option>
       </select>

       <SelectField value={x} onChange={e => setX(e.target.value)}>
         <option value="">All</option>
         <option value="a">A</option>
       </SelectField>

   Two things it handles that a bare swap could not:

   • Radix rejects `value=""` — it reserves the empty string for "nothing
     selected". 22 call sites use `<option value="">All</option>` as their
     placeholder. Those are mapped to an internal sentinel and mapped back on
     the way out, so the caller still sees "".

   • `<option>` children are read rather than re-authored, so option labels,
     ordering, `disabled` and grouping survive untouched.

   Limitation worth knowing: options must be direct children, inside a fragment
   or inside an `<optgroup>` — the three shapes `<select>` itself accepts. A
   custom component that *returns* options cannot be read without rendering it,
   so those few call sites need the Radix parts directly.
   ══════════════════════════════════════════════════════════════════════════ */

/** Radix treats "" as "no selection", so an empty-valued option needs a stand-in. */
const EMPTY = "__empty__";

export interface SelectFieldProps {
    value?: string | number | null;
    disabled?: boolean;
    required?: boolean;
    name?: string;
    id?: string;
    title?: string;
    tabIndex?: number;
    autoFocus?: boolean;
    onBlur?: React.FocusEventHandler<HTMLButtonElement>;
    onFocus?: React.FocusEventHandler<HTMLButtonElement>;
    "aria-label"?: string;
    "aria-labelledby"?: string;
    "aria-describedby"?: string;
    "aria-invalid"?: boolean | "true" | "false";
    "data-testid"?: string;
    /** Receives a `{ target: { value, name } }` shape, like the element it replaces. */
    onChange?: (event: { target: { value: string; name?: string } }) => void;
    /** Shown when nothing is selected and no empty-valued option was supplied */
    placeholder?: string;
    /** Applied to the trigger */
    className?: string;
    /** Applied to the popup */
    contentClassName?: string;
    triggerSize?: "sm" | "default";
    children?: React.ReactNode;
}

/* Only these forward to the trigger. `<select>` accepts attributes a button
   does not — `autoComplete`, `form`, `size` — and spreading the full element
   surface onto Radix's Trigger is a type error, not a silent no-op. */

interface ParsedOption {
    value: string;
    label: React.ReactNode;
    disabled?: boolean;
}

/** Flattens `<option>` / `<optgroup>` children into a list Radix can render. */
function parseOptions(children: React.ReactNode): {
    options: ParsedOption[];
    placeholderFromOption?: string;
} {
    const options: ParsedOption[] = [];
    let placeholderFromOption: string | undefined;

    const walk = (nodes: React.ReactNode) => {
        React.Children.forEach(nodes, (child) => {
            if (!React.isValidElement(child)) return;

            // Fragments and <optgroup> are containers, not options.
            if (child.type === React.Fragment || child.type === "optgroup") {
                walk((child.props as any).children);
                return;
            }
            if (child.type !== "option") return;

            const props = child.props as any;
            const raw = props.value ?? "";
            const value = String(raw);
            const label = props.children ?? value;

            // An empty-valued option is the native placeholder idiom.
            if (value === "") {
                if (typeof label === "string") placeholderFromOption = label;
                options.push({ value: EMPTY, label, disabled: props.disabled });
                return;
            }
            options.push({ value, label, disabled: props.disabled });
        });
    };

    walk(children);
    return { options, placeholderFromOption };
}

export const SelectField = React.forwardRef<HTMLButtonElement, SelectFieldProps>(
    (
        {
            value,
            onChange,
            placeholder,
            className,
            contentClassName,
            triggerSize = "default",
            children,
            disabled,
            required,
            name,
            id,
            ...rest
        },
        ref
    ) => {
        const { options, placeholderFromOption } = React.useMemo(
            () => parseOptions(children),
            [children]
        );

        const current = value == null || value === "" ? EMPTY : String(value);
        // Only bind a value Radix actually has an item for, or it clears itself.
        const known = options.some((o) => o.value === current);

        const handle = (next: string) => {
            onChange?.({ target: { value: next === EMPTY ? "" : next, name } });
        };

        return (
            <Select
                value={known ? current : undefined}
                onValueChange={handle}
                disabled={disabled}
                required={required}
                name={name}
            >
                <SelectTrigger
                    ref={ref}
                    id={id}
                    size={triggerSize}
                    className={cn("w-full", className)}
                    {...rest}
                >
                    <SelectValue placeholder={placeholder ?? placeholderFromOption ?? "Select…"} />
                </SelectTrigger>

                <SelectContent className={contentClassName}>
                    {options.map((o) => (
                        <SelectItem key={o.value} value={o.value} disabled={o.disabled}>
                            {o.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        );
    }
);
SelectField.displayName = "SelectField";

export default SelectField;
